import type Phaser from 'phaser';
export { formatEuro } from '../../../shared/scoring.js';

/**
 * Shared UI styling for the game HUD and the editions' overlays.
 * Demo look derived from the customer's design sketch: blue/orange pixel world,
 * dark navy panels with a light blue frame, orange progress bar.
 */
export const UI_FONT = 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif';

/** heavy weight for numbers and headlines (falls back to bold where the font has no 900) */
export const UI_HEAVY = '900';

/**
 * Font sizes of the big texts over the game window (waiting mode, "Du bist dran", "Spiel beginnt", end
 * screen) in 1/1000 of the width of the window's largest visible part – one size for headlines, one for
 * texts, the same line spacing everywhere, so every screen looks alike.
 */
export const TYPE = {
  /** headlines, amounts */
  title: 84,
  /** texts below, labels */
  text: 50,
  /** extra space between lines, × font size */
  lineSpacing: 0.12,
} as const;

export const PALETTE = {
  ink: 0x0b2259,
  navy: 0x123a8c,
  blue: 0x1f6ae0,
  blueLight: 0x4b92f2,
  blueHi: 0x8cc0ff,
  orangeDark: 0xd9620b,
  orange: 0xff9419,
  orangeLight: 0xffb54a,
  orangeHi: 0xffd690,
  /** time bar */
  yellowDark: 0xd99a00,
  yellow: 0xffc21a,
  yellowHi: 0xffe58a,
  white: 0xffffff,
} as const;

/** the same colours as CSS strings (for texts) */
export const CSS = {
  ink: '#0b2259',
  label: '#9cc6ff',
  text: '#ffffff',
  muted: '#cfe4ff',
  orange: '#ff9419',
  orangeLight: '#ffb54a',
  /** a coin lost by a hit */
  lost: '#ff7a66',
  /** Grundzulage (bonus coins) */
  bonus: '#9fd0ff',
  /** special coins (their colour) */
  starter: '#3ee07a',
  baby: '#ff5fb8',
  gold: '#ffc21a',
} as const;

export interface PanelOptions {
  /** fill alpha, default 0.88 */
  alpha?: number;
  radius: number;
  /** frame width */
  border: number;
  fill?: number;
  borderColor?: number;
}

/** Dark navy panel with a light blue frame (HUD boxes, result panel, QR frame). */
export function drawPanel(g: Phaser.GameObjects.Graphics, x: number, y: number, w: number, h: number, o: PanelOptions): void {
  const r = Math.min(o.radius, w / 2, h / 2);
  g.fillStyle(o.fill ?? PALETTE.ink, o.alpha ?? 0.88);
  g.fillRoundedRect(x, y, w, h, r);
  g.lineStyle(o.border, o.borderColor ?? PALETTE.blueLight, 1);
  g.strokeRoundedRect(x + o.border / 2, y + o.border / 2, w - o.border, h - o.border, Math.max(0, r - o.border / 2));
}

/**
 * Chunky button: coloured face with a darker "pixel" edge at the bottom and a dark outline.
 * Drawn into `g` centred on (0, 0).
 */
export function drawButton(g: Phaser.GameObjects.Graphics, w: number, h: number, face: number, edge: number, unit: number): void {
  const r = unit * 3;
  const depth = unit * 2;
  g.clear();
  g.fillStyle(PALETTE.ink, 1);
  g.fillRoundedRect(-w / 2, -h / 2, w, h, r);
  g.fillStyle(edge, 1);
  g.fillRoundedRect(-w / 2 + unit, -h / 2 + unit, w - unit * 2, h - unit * 2, r - unit);
  g.fillStyle(face, 1);
  g.fillRoundedRect(-w / 2 + unit, -h / 2 + unit, w - unit * 2, h - unit * 2 - depth, r - unit);
}

/** Seconds → "mm:ss" (rounded up, so 0:00 only shows when the time is really over). */
export function formatClock(sec: number): string {
  const s = Math.max(0, Math.ceil(sec));
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}
